import { getCleanedAndSortedPurchasables, IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchaseStateWithCustomerRelationship } from "@smartrr/shared/entities/PurchaseState";
import { captureException } from "@smartrr/shared/utils/captureException";
import {
  MappedPurchasableVariant,
  useVariantShopifyIDToPurchasableMap,
  useVariantToPurchasableMap,
} from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";

export interface SubscriptionPurchasablesContextType {
  purchasables: IPurchasable[];
  variantToPurchasableMap: MappedPurchasableVariant;
  variantShopifyIdToPurchasableMap: MappedPurchasableVariant;
  // true until the first request for the subscription's purchasables settles
  isInitialLoading: boolean;
  // true while any request is in flight, including refetches after line items change
  isFetching: boolean;
  // true when the latest request succeeded, so the maps can be trusted to detect removed products
  hasLoaded: boolean;
}

const SubscriptionPurchasablesContext = React.createContext<SubscriptionPurchasablesContextType>({
  purchasables: [],
  variantToPurchasableMap: {},
  variantShopifyIdToPurchasableMap: {},
  isInitialLoading: false,
  isFetching: false,
  hasLoaded: false,
});

export const useSubscriptionPurchasables = () => useContext(SubscriptionPurchasablesContext);

interface SubscriptionPurchasablesProviderProps {
  customerPurchaseState: IPurchaseStateWithCustomerRelationship;
  children: React.ReactNode;
}

export function SubscriptionPurchasablesProvider({
  customerPurchaseState,
  children,
}: SubscriptionPurchasablesProviderProps): JSX.Element {
  const { addToast } = useToast();
  const [purchasables, setPurchasables] = useState<IPurchasable[]>([]);
  // key of the variants the current purchasables were requested for, null until the first request settles
  const [settledVariantsKey, setSettledVariantsKey] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Refetch only when the set of referenced variants changes (add, swap, replace, remove)
  const variantsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const lineItem of customerPurchaseState.stLineItems ?? []) {
      if (lineItem.vnt?.id !== undefined) {
        ids.add(lineItem.vnt.id);
      }
      for (const bundleLine of lineItem.bundleLines ?? []) {
        ids.add(`bundle-${bundleLine.id}`);
      }
    }
    return [...ids].sort().join(",");
  }, [customerPurchaseState.stLineItems]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchPurchasables = async () => {
      let succeeded = false;
      try {
        const res = await typedFrontendVendorApi.getReq("/purchase-state/:shopifySubscriptionId/purchasables", {
          params: { shopifySubscriptionId: customerPurchaseState.id },
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        if (res.type === "success") {
          setPurchasables(getCleanedAndSortedPurchasables(res.body));
          succeeded = true;
        } else {
          addToast("Error loading subscription products");
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        captureException("Error loading subscription products", error);
        addToast("Error loading subscription products");
      }

      setHasLoaded(succeeded);
      setSettledVariantsKey(variantsKey);
    };

    fetchPurchasables();

    // Cancels the in-flight request when the line items change or the page unmounts
    return () => abortController.abort();
  }, [customerPurchaseState.id, variantsKey]);

  // Derived during render so consumers see a refetch as in flight in the same render the line items change
  const isInitialLoading = settledVariantsKey === null;
  const isFetching = settledVariantsKey !== variantsKey;

  const variantToPurchasableMap = useVariantToPurchasableMap(purchasables);
  const variantShopifyIdToPurchasableMap = useVariantShopifyIDToPurchasableMap(purchasables);

  return (
    <SubscriptionPurchasablesContext.Provider
      value={{
        purchasables,
        variantToPurchasableMap,
        variantShopifyIdToPurchasableMap,
        isInitialLoading,
        isFetching,
        hasLoaded,
      }}
    >
      {children}
    </SubscriptionPurchasablesContext.Provider>
  );
}
